/** @jsxImportSource @emotion/react */
import React, { useMemo, useState } from 'react';
import { Breadcrumb, Button, Card, Col, Descriptions, Empty, Popconfirm, Row, Space, Spin, Statistic, Table, Tag, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import MainLayout from '../../components/common/MainLayout';
import i18n, { formatDateTime as formatDateTimeI18n, formatNumber } from '../../i18n';
import {
  useGetStateChangesQuery,
  useGetStateSnapshotsQuery,
  useGetSystemMetricsQuery,
  type AdminActiveSessionMetrics,
} from '../../api/adminApi';
import { useCancelSimSessionMutation } from '../../api/simulationApi';

type AdminRecentJob = NonNullable<ReturnType<typeof useGetSystemMetricsQuery>['data']>['recentJobs'][number];
type AdminStateChange = NonNullable<ReturnType<typeof useGetStateChangesQuery>['data']>['items'][number];
type AdminStateSnapshot = NonNullable<ReturnType<typeof useGetStateSnapshotsQuery>['data']>['items'][number];

const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : formatDateTimeI18n(date, 'short');
};

const formatBytesMb = (value?: number) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return i18n.t('admin:systemMetrics.process.megabytes', {
    value: formatNumber(value / (1024 * 1024), { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
  });
};

const statusColor = (status: string) => {
  if (status === 'active') return 'green';
  if (status === 'paused') return 'gold';
  if (status === 'completed') return 'blue';
  if (status === 'cancelled') return 'red';
  return 'default';
};

const getSessionColumns = (
  opts: {
    onOpenFarm: (farmId: string) => void;
    onCancelSession: (sessionId: string) => Promise<void>;
    cancellingSessionId: string | null;
    t: TFunction;
  }
): ColumnsType<AdminActiveSessionMetrics> => [
  {
    title: opts.t('systemMetrics.columns.session'),
    dataIndex: 'id',
    width: 230,
    render: (value: string) => <span>{value.slice(0, 8)}…</span>,
  },
  {
    title: opts.t('systemMetrics.columns.farm'),
    dataIndex: 'farmName',
    render: (value: string, record) => value || record.farmId,
  },
  {
    title: opts.t('systemMetrics.columns.mode'),
    dataIndex: 'mode',
    render: (mode: string) => <Tag>{mode}</Tag>,
  },
  {
    title: opts.t('common:labels.status'),
    dataIndex: 'status',
    render: (status: string) => <Tag color={statusColor(status)}>{status}</Tag>,
  },
  {
    title: opts.t('systemMetrics.columns.simDate'),
    dataIndex: 'simDate',
    render: (value?: string | null) => value || '—',
  },
  {
    title: opts.t('systemMetrics.columns.plantings'),
    dataIndex: 'plantingsCount',
  },
  {
    title: opts.t('systemMetrics.columns.cpvSim'),
    dataIndex: 'cpvSimRows',
  },
  {
    title: opts.t('systemMetrics.columns.stateChanges'),
    dataIndex: 'stateChangesCount',
  },
  {
    title: opts.t('systemMetrics.columns.lastSnapshot'),
    dataIndex: 'lastSnapshotAt',
    render: (value?: string | null) => formatDateTime(value),
  },
  {
    title: opts.t('common:labels.actions'),
    key: 'actions',
    width: 190,
    render: (_, record) => (
      <Space>
        <Button size="small" onClick={() => opts.onOpenFarm(record.farmId)}>
          {opts.t('systemMetrics.sessions.openFarm')}
        </Button>
        <Popconfirm
          title={opts.t('systemMetrics.sessions.cancelConfirmTitle')}
          description={opts.t('systemMetrics.sessions.cancelConfirmDescription')}
          okText={opts.t('systemMetrics.sessions.cancelSession')}
          cancelText={opts.t('common:actions.back')}
          onConfirm={() => opts.onCancelSession(record.id)}
        >
          <Button
            size="small"
            danger
            loading={opts.cancellingSessionId === record.id}
          >
            {opts.t('systemMetrics.sessions.stop')}
          </Button>
        </Popconfirm>
      </Space>
    ),
  },
];

const jobColumns = (t: TFunction): ColumnsType<AdminRecentJob> => [
  {
    title: t('systemMetrics.columns.job'),
    dataIndex: 'id',
    width: 140,
    render: (value: string) => <span>{value.slice(0, 8)}…</span>,
  },
  {
    title: t('systemMetrics.columns.farm'),
    dataIndex: 'farmName',
  },
  {
    title: t('common:labels.type'),
    dataIndex: 'jobType',
    render: (value: string) => <Tag>{value}</Tag>,
  },
  {
    title: t('common:labels.status'),
    dataIndex: 'status',
    render: (status: string) => <Tag color={statusColor(status)}>{status}</Tag>,
  },
  {
    title: t('systemMetrics.columns.progress'),
    render: (_, row) => t('systemMetrics.jobs.progress', {
      processed: row.processedItems,
      total: row.totalItems,
      failed: row.failedItems,
    }),
  },
  {
    title: t('systemMetrics.columns.requestedAt'),
    dataIndex: 'requestedAt',
    render: (value: string) => formatDateTime(value),
  },
  {
    title: t('systemMetrics.columns.finishedAt'),
    dataIndex: 'finishedAt',
    render: (value?: string | null) => formatDateTime(value),
  },
];

const stateChangeColumns = (t: TFunction): ColumnsType<AdminStateChange> => [
  {
    title: t('systemMetrics.columns.time'),
    dataIndex: 'createdAt',
    width: 170,
    render: (value: string) => formatDateTime(value),
  },
  {
    title: t('systemMetrics.columns.farm'),
    dataIndex: 'farmName',
    render: (value: string | null, row) => value || row.farmId,
  },
  {
    title: t('systemMetrics.columns.session'),
    dataIndex: 'simSessionId',
    render: (value: string | null) => (value ? `${value.slice(0, 8)}…` : '—'),
  },
  {
    title: t('systemMetrics.columns.entity'),
    render: (_, row) => (
      <Space direction="vertical" size={0}>
        <Tag>{row.entityType}</Tag>
        <span>{row.entityId.slice(0, 8)}…</span>
      </Space>
    ),
  },
  {
    title: t('systemMetrics.columns.property'),
    render: (_, row) => row.propertyName || row.propertyCode || row.propertyId,
  },
  {
    title: t('systemMetrics.columns.value'),
    dataIndex: 'resultingValue',
    ellipsis: true,
    render: (value: string) => (value && value.length > 120 ? `${value.slice(0, 120)}…` : value),
  },
  {
    title: t('systemMetrics.columns.source'),
    dataIndex: 'sourceType',
    render: (value: string) => <Tag>{value}</Tag>,
  },
];

const stateSnapshotColumns = (t: TFunction): ColumnsType<AdminStateSnapshot> => [
  {
    title: t('systemMetrics.columns.time'),
    dataIndex: 'createdAt',
    width: 170,
    render: (value: string) => formatDateTime(value),
  },
  {
    title: t('systemMetrics.columns.farm'),
    dataIndex: 'farmName',
    render: (value: string | null, row) => value || row.farmId,
  },
  {
    title: t('systemMetrics.columns.session'),
    dataIndex: 'simSessionId',
    render: (value: string | null) => (value ? `${value.slice(0, 8)}…` : '—'),
  },
  {
    title: t('systemMetrics.columns.entity'),
    render: (_, row) => (
      <Space direction="vertical" size={0}>
        <Tag>{row.entityType}</Tag>
        <span>{row.entityId.slice(0, 8)}…</span>
      </Space>
    ),
  },
  {
    title: t('systemMetrics.columns.reason'),
    dataIndex: 'reason',
    render: (value: string) => <Tag>{value}</Tag>,
  },
  {
    title: t('systemMetrics.columns.lane'),
    dataIndex: 'lane',
    render: (value: string | null) => (value ? <Tag>{value}</Tag> : '—'),
  },
  {
    title: t('systemMetrics.columns.simDate'),
    dataIndex: 'simDate',
    render: (value: string | null) => value || '—',
  },
  {
    title: t('systemMetrics.columns.snapshotData'),
    dataIndex: 'snapshotPreview',
    ellipsis: true,
    render: (value: string | null) => (value && value.length > 160 ? `${value.slice(0, 160)}…` : value || '—'),
  },
];

const AdminSystemMetricsPage: React.FC = () => {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const [cancellingSessionId, setCancellingSessionId] = useState<string | null>(null);
  const [cancelSimSession] = useCancelSimSessionMutation();

  const { data, isLoading, isFetching, isError, refetch } = useGetSystemMetricsQuery();
  const {
    data: stateChanges,
    isLoading: loadingStateChanges,
    isFetching: fetchingStateChanges,
    refetch: refetchStateChanges,
  } = useGetStateChangesQuery({ limit: 50 });
  const {
    data: stateSnapshots,
    isLoading: loadingStateSnapshots,
    isFetching: fetchingStateSnapshots,
    refetch: refetchStateSnapshots,
  } = useGetStateSnapshotsQuery({ limit: 50 });

  const overview = data?.overview;

  const cards = useMemo(
    () => [
      { title: t('systemMetrics.overview.farms'), value: overview?.farmsCount ?? 0 },
      { title: t('systemMetrics.overview.activePlantings'), value: overview?.activePlantingsCount ?? 0 },
      { title: t('systemMetrics.overview.simSessionsTotal'), value: overview?.simSessionsTotal ?? 0 },
      { title: t('systemMetrics.overview.simSessionsActive'), value: overview?.simSessionsActive ?? 0 },
      { title: t('systemMetrics.overview.stateChanges24h'), value: overview?.stateChanges24h ?? 0 },
      { title: t('systemMetrics.overview.snapshots24h'), value: overview?.stateSnapshots24h ?? 0 },
    ],
    [overview, t]
  );

  const jobCards = useMemo(
    () => [
      { title: t('systemMetrics.jobs.total'), value: data?.jobsOverview?.total ?? 0 },
      { title: t('systemMetrics.jobs.pending'), value: data?.jobsOverview?.pending ?? 0 },
      { title: t('systemMetrics.jobs.running'), value: data?.jobsOverview?.running ?? 0 },
      { title: t('systemMetrics.jobs.failed'), value: data?.jobsOverview?.failed ?? 0 },
    ],
    [data, t]
  );

  const handleOpenFarm = async (_farmId: string) => {
    navigate('/saves?type=free');
  };

  const handleCancelSession = async (sessionId: string) => {
    try {
      setCancellingSessionId(sessionId);
      await cancelSimSession(sessionId).unwrap();
      message.success(t('systemMetrics.sessions.stopped'));
      refetch();
    } catch (error: any) {
      message.error(error?.data?.message || t('systemMetrics.sessions.stopFailed'));
    } finally {
      setCancellingSessionId(null);
    }
  };

  const sessionColumns = getSessionColumns({
    onOpenFarm: handleOpenFarm,
    onCancelSession: handleCancelSession,
    cancellingSessionId,
    t,
  });

  return (
    <MainLayout
      breadcrumb={
        <Breadcrumb
          items={[
            { title: t('systemMetrics.breadcrumb.admin') },
            { title: t('systemMetrics.breadcrumb.system') },
          ]}
        />
      }
    >
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Card
          title={t('systemMetrics.title')}
          extra={
            <Button onClick={() => refetch()} loading={isFetching}>
              {t('common:actions.refresh')}
            </Button>
          }
        >
          <Spin spinning={isLoading}>
            {isError || !data ? (
              <Empty description={t('systemMetrics.loadFailed')} />
            ) : (
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Row gutter={[12, 12]}>
                  {cards.map((item) => (
                    <Col key={item.title} xs={24} sm={12} md={8} lg={6}>
                      <Card>
                        <Statistic title={item.title} value={item.value} />
                      </Card>
                    </Col>
                  ))}
                </Row>

                <Card title={t('systemMetrics.process.title')}>
                  <Descriptions size="small" column={2}>
                    <Descriptions.Item label={t('systemMetrics.process.timestamp')}>{formatDateTime(data.timestamp)}</Descriptions.Item>
                    <Descriptions.Item label="NODE_ENV">{data.process.nodeEnv}</Descriptions.Item>
                    <Descriptions.Item label={t('systemMetrics.process.uptime')}>
                      {t('systemMetrics.process.seconds', { value: data.process.uptimeSeconds })}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('systemMetrics.process.scheduler')}>
                      <Tag color={data.scheduler.enabled ? 'green' : 'red'}>
                        {data.scheduler.enabled ? t('common:states.enabled') : t('common:states.disabled')}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label={t('systemMetrics.process.schedulerInterval')}>
                      {data.scheduler.intervalMs
                        ? t('systemMetrics.process.milliseconds', { value: data.scheduler.intervalMs })
                        : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('systemMetrics.process.memoryRss')}>{formatBytesMb(data.process.memory.rss)}</Descriptions.Item>
                    <Descriptions.Item label={t('systemMetrics.process.heapUsed')}>{formatBytesMb(data.process.memory.heapUsed)}</Descriptions.Item>
                    <Descriptions.Item label={t('systemMetrics.process.heapTotal')}>{formatBytesMb(data.process.memory.heapTotal)}</Descriptions.Item>
                  </Descriptions>
                </Card>

                <Card title={t('systemMetrics.sessions.title')}>
                  <Table
                    rowKey={(row) => row.id}
                    dataSource={data.activeSessions}
                    columns={sessionColumns}
                    pagination={{ pageSize: 10 }}
                    locale={{ emptyText: t('systemMetrics.sessions.empty') }}
                    scroll={{ x: 1100 }}
                  />
                </Card>

                <Card title={t('systemMetrics.jobs.title')}>
                  <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
                    {jobCards.map((item) => (
                      <Col key={item.title} xs={24} sm={12} md={6}>
                        <Card>
                          <Statistic title={item.title} value={item.value} />
                        </Card>
                      </Col>
                    ))}
                  </Row>

                  <Table
                    rowKey={(row) => row.id}
                    dataSource={data.recentJobs}
                    columns={jobColumns(t)}
                    pagination={{ pageSize: 10 }}
                    locale={{ emptyText: t('systemMetrics.jobs.empty') }}
                    scroll={{ x: 1000 }}
                  />
                </Card>

                <Card
                  title={t('systemMetrics.stateChanges.title')}
                  extra={
                    <Button onClick={() => refetchStateChanges()} loading={fetchingStateChanges}>
                      {t('common:actions.refresh')}
                    </Button>
                  }
                >
                  <Spin spinning={loadingStateChanges}>
                    <Table
                      rowKey={(row) => row.id}
                      dataSource={stateChanges?.items || []}
                      columns={stateChangeColumns(t)}
                      pagination={{ pageSize: 10 }}
                      locale={{ emptyText: t('systemMetrics.stateChanges.empty') }}
                      scroll={{ x: 1200 }}
                    />
                  </Spin>
                </Card>

                <Card
                  title={t('systemMetrics.stateSnapshots.title')}
                  extra={
                    <Button onClick={() => refetchStateSnapshots()} loading={fetchingStateSnapshots}>
                      {t('common:actions.refresh')}
                    </Button>
                  }
                >
                  <Spin spinning={loadingStateSnapshots}>
                    <Table
                      rowKey={(row) => row.id}
                      dataSource={stateSnapshots?.items || []}
                      columns={stateSnapshotColumns(t)}
                      pagination={{ pageSize: 10 }}
                      locale={{ emptyText: t('systemMetrics.stateSnapshots.empty') }}
                      scroll={{ x: 1200 }}
                    />
                  </Spin>
                </Card>
              </Space>
            )}
          </Spin>
        </Card>
      </Space>
    </MainLayout>
  );
};

export default AdminSystemMetricsPage;
